import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminCard, StatusPill, formatCHF, btnGhost, btnPrimary, EmptyState } from "@/lib/admin/ui";
import { useState } from "react";
import {
  ArrowLeft, Phone, Mail, MessageCircle, Globe, Receipt, Ticket, FileText,
  Save, Building2, MapPin, CreditCard, User, Activity, FolderOpen,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/clients/$id")({
  component: ClientProfile,
});

function ClientProfile() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"invoices" | "tickets" | "files" | "activity" | "notes">("invoices");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-client", id],
    queryFn: async () => {
      const [project, invoices, tickets, subs, files, activity] = await Promise.all([
        supabase.from("projects").select("*").eq("id", id).maybeSingle(),
        supabase.from("invoices").select("*").eq("project_id", id).order("issued_at", { ascending: false }),
        supabase.from("tickets").select("*").eq("project_id", id).order("created_at", { ascending: false }),
        supabase.from("subscriptions").select("*").eq("project_id", id),
        supabase.from("project_files").select("*").eq("project_id", id).order("created_at", { ascending: false }).limit(20),
        supabase.from("activity_log").select("*").eq("entity_id", id).order("created_at", { ascending: false }).limit(30),
      ]);
      if (!project.data) return null;
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", project.data.owner_id).maybeSingle();
      return { project: project.data, profile, invoices: invoices.data ?? [], tickets: tickets.data ?? [], subs: subs.data ?? [], files: files.data ?? [], activity: activity.data ?? [] };
    },
  });

  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  if (isLoading) return <div className="text-white/40 text-sm">Caricamento…</div>;
  if (!data) throw notFound();

  const { project, profile, invoices, tickets, subs, files, activity } = data;
  const phone = profile?.phone ?? "";
  const email = profile?.email ?? "";
  const paidTotal = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const pendingTotal = invoices.filter((i) => i.status !== "paid").reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const openTickets = tickets.filter((t) => !["completed", "closed"].includes(t.status)).length;

  const saveNotes = async () => {
    setSavingNotes(true);
    const val = notes || profile?.internal_notes || "";
    const { error } = await supabase.from("profiles").update({ internal_notes: val }).eq("id", project.owner_id);
    setSavingNotes(false);
    if (error) return toast.error(error.message);
    toast.success("Note salvate");
    qc.invalidateQueries({ queryKey: ["admin-client", id] });
  };

  const whatsappHref = phone ? `https://wa.me/${phone.replace(/[^0-9]/g, "")}` : "#";
  const emailHref = email ? `mailto:${email}` : "#";

  return (
    <div className="max-w-[1600px] space-y-6">
      {/* Back + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/admin/clients" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white">
          <ArrowLeft className="h-3.5 w-3.5" /> Torna ai clienti
        </Link>
        <div className="flex items-center gap-1.5">
          <a href={`tel:${phone}`} className={btnGhost}><Phone className="h-3.5 w-3.5" /> Call</a>
          <a href={emailHref} className={btnGhost}><Mail className="h-3.5 w-3.5" /> Email</a>
          <a href={whatsappHref} target="_blank" rel="noreferrer" className={btnGhost}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
          {project.website_url && <a href={project.website_url} target="_blank" rel="noreferrer" className={btnGhost}><Globe className="h-3.5 w-3.5" /> Website</a>}
          <button className={btnPrimary}><Receipt className="h-3.5 w-3.5" /> Nuova fattura</button>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center text-2xl font-bold text-black shadow-lg shadow-fuchsia-500/20">
          {(profile?.company ?? profile?.full_name ?? "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold">{profile?.company ?? profile?.full_name ?? "Cliente"}</h1>
            <StatusPill status={project.plan ?? "—"} tone="violet" />
            <StatusPill status={project.kanban_status ?? project.status ?? "—"} />
          </div>
          <div className="text-sm text-white/50 mt-1">{project.name} · {profile?.full_name}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Fatturato</div>
          <div className="text-2xl font-semibold tabular-nums text-emerald-300">{formatCHF(paidTotal)}</div>
          {pendingTotal > 0 && <div className="text-xs text-amber-300">+ {formatCHF(pendingTotal)} pending</div>}
        </div>
      </div>

      {/* Progress bar */}
      <AdminCard className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Progetto {project.name}</div>
          <div className="text-xs text-white/60">{project.progress ?? 0}% · {project.current_step ?? "—"}</div>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-fuchsia-500 via-violet-400 to-cyan-400" style={{ width: `${project.progress ?? 0}%` }} />
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-white/50">
          <span>PM: {project.project_manager ?? "—"}</span>
          {project.delivery_date && <span>Consegna: {format(new Date(project.delivery_date), "d MMM yyyy", { locale: it })}</span>}
          <span className="ml-auto">Revisioni residue: {project.revisions_left ?? 3}</span>
        </div>
      </AdminCard>

      {/* Info grid */}
      <div className="grid gap-4 lg:grid-cols-3">
        <InfoCard icon={User} title="Contatto">
          <Row label="Nome" value={profile?.full_name} />
          <Row label="Referente" value={profile?.contact_person} />
          <Row label="Email" value={profile?.email} />
          <Row label="Telefono" value={profile?.phone} />
        </InfoCard>
        <InfoCard icon={Building2} title="Azienda">
          <Row label="Ragione sociale" value={profile?.company} />
          <Row label="Indirizzo billing" value={profile?.billing_address} />
          <Row label="Piano" value={project.plan} />
          <Row label="PM" value={project.project_manager} />
        </InfoCard>
        <InfoCard icon={CreditCard} title="Abbonamento">
          {subs.length === 0 ? (
            <div className="text-xs text-white/40">Nessun abbonamento attivo</div>
          ) : subs.map((s) => (
            <div key={s.id} className="text-xs space-y-1">
              <div className="flex justify-between"><span className="text-white/50">Piano</span><span className="font-semibold uppercase">{s.plan}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Prezzo</span><span>{s.currency} {Number(s.price).toLocaleString("it-CH")}/{s.billing_cycle}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Status</span><StatusPill status={s.status} /></div>
              {s.renewal_date && <div className="flex justify-between"><span className="text-white/50">Rinnovo</span><span>{format(new Date(s.renewal_date), "d MMM yyyy", { locale: it })}</span></div>}
            </div>
          ))}
        </InfoCard>
      </div>

      {/* Tabs */}
      <div>
        <div className="flex items-center gap-1 border-b border-white/[0.06]">
          {[
            { id: "invoices", label: `Fatture (${invoices.length})`, icon: Receipt },
            { id: "tickets", label: `Ticket (${openTickets}/${tickets.length})`, icon: Ticket },
            { id: "files", label: `File (${files.length})`, icon: FolderOpen },
            { id: "activity", label: "Attività", icon: Activity },
            { id: "notes", label: "Note interne", icon: FileText },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id as typeof tab)}
              className={`px-3 py-2 text-xs flex items-center gap-1.5 border-b-2 transition ${tab === t.id ? "border-fuchsia-400 text-white" : "border-transparent text-white/50 hover:text-white"}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <div className="pt-4">
          {tab === "invoices" && <InvoicesTab invoices={invoices} />}
          {tab === "tickets" && <TicketsTab tickets={tickets} />}
          {tab === "files" && <FilesTab files={files} />}
          {tab === "activity" && <ActivityTab items={activity} />}
          {tab === "notes" && (
            <AdminCard className="p-4">
              <textarea defaultValue={profile?.internal_notes ?? ""} onChange={(e) => setNotes(e.target.value)}
                placeholder="Note interne visibili solo agli admin…"
                className="w-full min-h-40 bg-transparent text-sm outline-none resize-none" />
              <div className="mt-3 flex justify-end">
                <button onClick={saveNotes} disabled={savingNotes} className={btnPrimary}><Save className="h-3.5 w-3.5" /> {savingNotes ? "Salvo…" : "Salva note"}</button>
              </div>
            </AdminCard>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }: { icon: React.ComponentType<{ className?: string }>; title: string; children: React.ReactNode }) {
  return (
    <AdminCard className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-white/50" />
        <div className="text-sm font-semibold">{title}</div>
      </div>
      <div className="space-y-1.5">{children}</div>
    </AdminCard>
  );
}
function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-white/40">{label}</span>
      <span className="text-white/80 truncate max-w-[60%] text-right">{value || "—"}</span>
    </div>
  );
}

function InvoicesTab({ invoices }: { invoices: Array<{ id: string; invoice_number: string; amount: number; currency: string; status: string; issued_at: string; due_date: string | null }> }) {
  if (invoices.length === 0) return <EmptyState icon={Receipt} title="Nessuna fattura" />;
  return (
    <AdminCard className="overflow-hidden">
      {invoices.map((i) => (
        <div key={i.id} className="grid grid-cols-[100px_1fr_120px_120px_100px] gap-3 px-4 py-3 text-sm border-b border-white/[0.04] last:border-0">
          <div className="font-mono text-xs text-white/50">{i.invoice_number}</div>
          <div className="text-white/70 text-xs">{format(new Date(i.issued_at), "d MMM yyyy", { locale: it })}</div>
          <div className="font-semibold tabular-nums">{i.currency} {Number(i.amount).toLocaleString("it-CH")}</div>
          <div className="text-xs text-white/50">{i.due_date ? format(new Date(i.due_date), "d MMM", { locale: it }) : "—"}</div>
          <StatusPill status={i.status} />
        </div>
      ))}
    </AdminCard>
  );
}
function TicketsTab({ tickets }: { tickets: Array<{ id: string; code: string; title: string; priority: string; status: string; created_at: string }> }) {
  if (tickets.length === 0) return <EmptyState icon={Ticket} title="Nessun ticket" />;
  return (
    <AdminCard className="overflow-hidden">
      {tickets.map((t) => (
        <div key={t.id} className="grid grid-cols-[90px_1fr_100px_120px] gap-3 px-4 py-3 text-sm border-b border-white/[0.04] last:border-0">
          <div className="font-mono text-xs text-white/50">{t.code}</div>
          <div className="truncate">{t.title}</div>
          <div className="text-xs">{t.priority}</div>
          <StatusPill status={t.status} />
        </div>
      ))}
    </AdminCard>
  );
}
function FilesTab({ files }: { files: Array<{ id: string; name: string; category?: string; size?: number; created_at: string }> }) {
  if (files.length === 0) return <EmptyState icon={FolderOpen} title="Nessun file caricato" />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {files.map((f) => (
        <AdminCard key={f.id} className="p-3 flex items-center gap-3">
          <FolderOpen className="h-5 w-5 text-white/40" />
          <div className="min-w-0">
            <div className="text-sm truncate">{f.name}</div>
            <div className="text-[10px] text-white/40">{f.category ?? "—"}</div>
          </div>
        </AdminCard>
      ))}
    </div>
  );
}
function ActivityTab({ items }: { items: Array<{ id: string; action: string; entity_type: string; created_at: string }> }) {
  if (items.length === 0) return <EmptyState icon={Activity} title="Nessuna attività" />;
  return (
    <AdminCard className="p-4">
      {items.map((a) => (
        <div key={a.id} className="flex items-center gap-3 py-2 text-sm border-b border-white/[0.04] last:border-0">
          <StatusPill status={a.action} />
          <span className="text-white/60">{a.entity_type}</span>
          <span className="ml-auto text-[11px] text-white/40">{format(new Date(a.created_at), "d MMM HH:mm", { locale: it })}</span>
        </div>
      ))}
    </AdminCard>
  );
}
